import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, PageLoader, StatusBadge, EmptyState, Btn, TextInput, TextArea } from "@/lib/ui";
import { MediaPicker, MediaImg } from "@/components/MediaPicker";
import { MessageSquareQuote, Plus, Trash2, Star, DownloadCloud, Check, X, PencilLine } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const STATUSES = ["pending", "approved", "rejected"];
const TONE = { pending: "warn", approved: "ok", rejected: "err" };
const BRANDS = ["", "Gopi Gadhvi Group", "Monarch Estates", "Monarch Design Studio", "The Art Forest", "The Wishing Well"];
const EMPTY = { name: "", role_or_company: "", quote: "", brand: "", avatar_url: "", rating: null, featured: false, status: "pending" };

function Editor({ item, onClose }) {
  const api = useApi();
  const qc = useQueryClient();
  const isNew = !item?.id;
  const [form, setForm] = useState(EMPTY);
  useEffect(() => { setForm(item?.id ? { ...EMPTY, ...item } : EMPTY); }, [item?.id]); // eslint-disable-line
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inval = () => { qc.invalidateQueries({ queryKey: ["testimonials"] }); qc.invalidateQueries({ queryKey: ["testimonials-stats"] }); };
  const payload = () => {
    const K = ["name", "role_or_company", "quote", "brand", "avatar_url", "rating", "featured", "status"];
    const p = {}; K.forEach((k) => { p[k] = form[k]; }); return p;
  };
  const save = useMutation({
    mutationFn: () => isNew ? api.post("/cms/testimonials", payload()) : api.patch(`/cms/testimonials/${item.id}`, payload()),
    onSuccess: () => { toast.success(isNew ? "Testimonial added" : "Saved"); inval(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: () => api.del(`/cms/testimonials/${item.id}`),
    onSuccess: () => { toast.success("Deleted"); inval(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4" data-testid="testimonial-editor">
      <div className="grid grid-cols-2 gap-3">
        <TextInput label="Name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="testimonial-name" />
        <TextInput label="Role / Company" value={form.role_or_company} onChange={(e) => set("role_or_company", e.target.value)} data-testid="testimonial-role" />
      </div>
      <TextArea label="Quote" rows={4} value={form.quote} onChange={(e) => set("quote", e.target.value)} data-testid="testimonial-quote" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="text-xs text-muted-foreground mb-1.5 block">Brand</span>
          <Select value={form.brand} onValueChange={(v) => set("brand", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="testimonial-brand"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{BRANDS.map((b) => <SelectItem key={b || "none"} value={b || "none"}>{b || "None"}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <TextInput label="Rating (1–5)" type="number" min={1} max={5} value={form.rating ?? ""} onChange={(e) => set("rating", e.target.value ? parseInt(e.target.value, 10) : null)} data-testid="testimonial-rating" />
      </div>
      <div>
        <span className="text-xs text-muted-foreground mb-1.5 block">Avatar</span>
        <div className="flex items-center gap-2">
          {form.avatar_url && <MediaImg src={form.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover border border-border" testid="testimonial-avatar-preview" />}
          <input className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm" placeholder="Avatar URL (optional)"
            value={form.avatar_url || ""} onChange={(e) => set("avatar_url", e.target.value)} data-testid="testimonial-avatar-url" />
          <MediaPicker folder="Testimonials" label="Pick" onPick={(url) => set("avatar_url", url)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="text-xs text-muted-foreground mb-1.5 block">Status</span>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="testimonial-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`testimonial-status-${s}`}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between border border-border rounded-md px-3">
          <div className="text-sm font-medium">Featured</div>
          <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="testimonial-featured-switch" />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Only <strong>approved</strong> testimonials appear on the website.</p>
      <div className="flex items-center gap-2 border-t border-border pt-4">
        <Btn onClick={() => save.mutate()} disabled={save.isPending || !form.name.trim() || !form.quote.trim()} data-testid="testimonial-save-btn">{save.isPending ? "Saving…" : isNew ? "Add" : "Save"}</Btn>
        {!isNew && (
          <AlertDialog>
            <AlertDialogTrigger asChild><Btn variant="danger" data-testid="testimonial-delete-btn"><Trash2 className="h-4 w-4" /> Delete</Btn></AlertDialogTrigger>
            <AlertDialogContent className="bg-surface border-border">
              <AlertDialogHeader><AlertDialogTitle>Delete this testimonial?</AlertDialogTitle><AlertDialogDescription>This removes the testimonial from {form.name}. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel data-testid="testimonial-delete-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del.mutate()} data-testid="testimonial-delete-confirm" className="bg-err text-white hover:bg-err/90">Delete</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

function ImportButton() {
  const api = useApi();
  const qc = useQueryClient();
  const imp = useMutation({
    mutationFn: () => api.post("/cms/testimonials/import"),
    onSuccess: (d) => { toast.success(`Imported ${d.imported} · ${d.skipped} skipped`); qc.invalidateQueries({ queryKey: ["testimonials"] }); qc.invalidateQueries({ queryKey: ["testimonials-stats"] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><Btn variant="outline" size="sm" data-testid="testimonial-import-btn"><DownloadCloud className="h-4 w-4" /> Import from live site</Btn></AlertDialogTrigger>
      <AlertDialogContent className="bg-surface border-border">
        <AlertDialogHeader><AlertDialogTitle>Import testimonials from the live website?</AlertDialogTitle><AlertDialogDescription>Copies existing testimonials into the Command Centre (already-approved ones stay approved, others land as pending for review). Nothing is overwritten.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel data-testid="testimonial-import-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={() => imp.mutate()} disabled={imp.isPending} data-testid="testimonial-import-confirm">{imp.isPending ? "Importing…" : "Import"}</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function Testimonials() {
  const api = useApi();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const { data: stats } = useQuery({ queryKey: ["testimonials-stats"], queryFn: () => api.get("/cms/testimonials/stats") });
  const { data: items = [], isLoading } = useQuery({ queryKey: ["testimonials", filter], queryFn: () => api.get(`/cms/testimonials${filter === "all" ? "" : `?status=${filter}`}`) });
  const quick = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/cms/testimonials/${id}`, { status }),
    onSuccess: () => { toast.success("Updated"); qc.invalidateQueries({ queryKey: ["testimonials"] }); qc.invalidateQueries({ queryKey: ["testimonials-stats"] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div data-testid="testimonials-page">
      <PageHeader kicker="Content · Website CMS" title="Testimonials">
        <ImportButton />
        <Btn size="sm" onClick={() => setEditing({})} data-testid="testimonial-new-btn"><Plus className="h-4 w-4" /> Add testimonial</Btn>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`testimonial-filter-${s}`}
            className={cn("text-xs rounded-full px-3 py-1.5 border transition-colors capitalize",
              filter === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground")}>
            {s}{stats ? ` · ${s === "all" ? (stats.total ?? 0) : (stats[s] ?? 0)}` : ""}
          </button>
        ))}
      </div>

      {isLoading ? <PageLoader /> : items.length === 0 ? (
        <Panel className="p-10"><EmptyState icon={MessageSquareQuote} title="No testimonials yet" hint="Add one or import from the live website. New testimonials need approval before they appear publicly."
          action={<Btn size="sm" className="mt-2" onClick={() => setEditing({})} data-testid="testimonial-empty-new"><Plus className="h-4 w-4" /> Add testimonial</Btn>} /></Panel>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="testimonials-grid">
          {items.map((it) => (
            <Panel key={it.id} className="p-4 flex flex-col gap-3" data-testid={`testimonial-card-${it.id}`}>
              <div className="flex items-start gap-3">
                {it.avatar_url ? <MediaImg src={it.avatar_url} alt={it.name} className="h-10 w-10 rounded-full object-cover border border-border" fallback={<div className="h-10 w-10 rounded-full bg-accent grid place-items-center text-sm font-semibold">{(it.name || "?")[0]}</div>} /> : <div className="h-10 w-10 rounded-full bg-accent grid place-items-center text-sm font-semibold">{(it.name || "?")[0]}</div>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {it.featured && <Star className="h-3.5 w-3.5 text-primary shrink-0" />}
                    <div className="font-medium truncate">{it.name}</div>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{it.role_or_company}{it.brand ? ` · ${it.brand}` : ""}</div>
                </div>
                <StatusBadge tone={TONE[it.status]} dot>{it.status}</StatusBadge>
              </div>
              <blockquote className="text-sm text-foreground/85 italic line-clamp-4">“{it.quote}”</blockquote>
              <div className="flex items-center justify-end gap-1.5 border-t border-border/60 pt-2">
                {it.status !== "approved" && <Btn variant="ghost" size="sm" onClick={() => quick.mutate({ id: it.id, status: "approved" })} data-testid={`testimonial-approve-${it.id}`}><Check className="h-4 w-4 text-ok" /> Approve</Btn>}
                {it.status !== "rejected" && <Btn variant="ghost" size="sm" onClick={() => quick.mutate({ id: it.id, status: "rejected" })} data-testid={`testimonial-reject-${it.id}`}><X className="h-4 w-4 text-err" /> Reject</Btn>}
                <Btn variant="ghost" size="icon" onClick={() => setEditing(it)} data-testid={`testimonial-open-${it.id}`}><PencilLine className="h-4 w-4" /></Btn>
              </div>
            </Panel>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-surface border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit testimonial" : "Add testimonial"}</DialogTitle><DialogDescription>Manage a customer testimonial. Only approved ones show on the website.</DialogDescription></DialogHeader>
          {editing && <Editor item={editing} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
